/**
 * sealed
 *
 * Seals a value to a server's published key before it is sent, so what crosses the mesh
 * and rests in a mailbox stream is ciphertext. This is the P-256 form of godev's
 * pkg/sealed (p256.go), made with WebCrypto alone: P-256 ECDH with an ephemeral key per
 * value, HKDF-SHA256, AES-256-GCM. The context both sides name (what the value is for)
 * is bound into the ciphertext, so a sealed value sent as something else does not open.
 *
 *   a8sealed:p256v1:<recipient key id>:<base64 of ephemeral public key, nonce, ciphertext>
 */

export const P256_PREFIX = "a8sealed:p256v1:";

const INFO = "a8sealed:p256v1";

type Bytes = Uint8Array<ArrayBuffer>;

function fromBase64(text: string): Bytes {
  const binary = atob(text.trim());
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function concat(...parts: Uint8Array[]): Bytes {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

/** The id a sealed value names its recipient's key by: the first 8 bytes of its SHA-256, in hex. */
export async function p256KeyId(publicKey: Uint8Array<ArrayBuffer>): Promise<string> {
  const sum = new Uint8Array(await crypto.subtle.digest("SHA-256", publicKey));
  return Array.from(sum.slice(0, 8), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Seals plaintext to a recipient's published P-256 public key (base64 of the uncompressed point), for the named context. */
export async function sealP256(recipientPublicKey: string, context: string, plaintext: string): Promise<string> {
  const subtle = crypto.subtle;
  const recipientRaw = fromBase64(recipientPublicKey);
  const recipient = await subtle.importKey("raw", recipientRaw, { name: "ECDH", namedCurve: "P-256" }, false, []);
  const ephemeral = await subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, ["deriveBits"]);
  const ephemeralRaw = new Uint8Array(await subtle.exportKey("raw", ephemeral.publicKey));
  const shared = await subtle.deriveBits({ name: "ECDH", public: recipient }, ephemeral.privateKey, 256);
  const keyMaterial = await subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  const text = new TextEncoder();
  const key = await subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: concat(ephemeralRaw, recipientRaw), info: text.encode(INFO) },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"],
  );
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await subtle.encrypt({ name: "AES-GCM", iv: nonce, additionalData: text.encode(context) }, key, text.encode(plaintext)),
  );
  return P256_PREFIX + (await p256KeyId(recipientRaw)) + ":" + toBase64(concat(ephemeralRaw, nonce, ciphertext));
}
